/**
 * UTM parameter capture and persistence.
 *
 * On first page load, reads utm_source, utm_medium, utm_campaign,
 * utm_content, and utm_term from the URL and stores them in
 * sessionStorage. The values persist across navigation within the
 * session but are cleared when the tab closes.
 *
 * getStoredUtm() returns the captured params for attaching to
 * the signup request.
 */

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "tf_utm";

/** Read UTMs from the current URL and persist if any are found. */
export function captureUtmParams(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  let found = false;

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      found = true;
    }
  }

  // Only overwrite if we found new UTMs (don't clobber earlier capture)
  if (found) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    } catch {
      // sessionStorage blocked (private browsing etc.)
    }
  }
}

/** Retrieve the captured UTM params (or empty object). */
export function getStoredUtm(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {  // catch-reason: a stored UTM blob that isn't JSON is absent
    return {};
  }
}

/** Also capture the landing page variant and referrer. */
export function getFullAttribution(): Record<string, string> {
  const utm = getStoredUtm();
  const attrs: Record<string, string> = {};

  for (const [k, v] of Object.entries(utm)) {
    if (v) attrs[k] = v;
  }

  // Add landing variant from cookie
  if (typeof document !== "undefined") {
    const match = document.cookie.match(/(?:^|; )tf_lp_variant=([^;]*)/);
    if (match) attrs.landing_variant = decodeURIComponent(match[1]);
  }

  // Add referrer (first-touch only)
  if (typeof document !== "undefined" && document.referrer) {
    try {
      const ref = new URL(document.referrer);
      // Only store external referrers
      if (ref.hostname !== window.location.hostname) {
        attrs.referrer = ref.hostname;
      }
    } catch {
      // Invalid referrer URL
    }
  }

  return attrs;
}
